import { memo, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { makeStyles } from "@mui/styles";
import { InfiniteScroll } from "components";
import { ConversationSelectors } from "redux-store";
import { useConversationContext } from "../ConversationContext";
import {
  CHAT_WRAPPER_ID,
  handleDisappearMsg,
  handleNewMessageList,
  isLocalMessage,
  refactorMessageList,
} from "../ViewMode.helper";
import MessageItem from "../MessageItem";
import { createSelector } from "reselect";
import ScrollTooltip from "components/ScrollTooltip";
import useQueryMessage from "../useQueryMessage";
import useDisappearMessage from "../useDisappearMessage";
import { SystemConstant } from "const";
import { convertString2JSON } from "utils";

const memoizedReduxState = createSelector(
  [
    ConversationSelectors.getSelectedGroupId,
    state => state.conversationRedux.scrollToChildId,
    state => state.conversationRedux.incomingMessages,
  ],
  (selectedGroupId, scrollToChildId, incomingMessages) => {
    return {
      selectedGroupId,
      scrollToChildId,
      incomingMessages: incomingMessages[selectedGroupId],
    };
  },
);

const ViewMode = () => {
  const classes = useStyles();

  const { groupDetail: groupDetailContext } = useConversationContext();
  const { selectedGroupId, scrollToChildId, incomingMessages } = useSelector(memoizedReduxState);
  const {
    showMessage,
    isLoadMore,
    scrollDataRef,
    messageListRef,
    getNewerMessage,
    getOlderMessage,
    setMessageList,
    scrollToLastMessage,
  } = useQueryMessage({ selectedGroupId, scrollToChildId });
  const { disappearListId } = useDisappearMessage({
    onCallback: disappearListId => {
      if (messageListRef.current.length > 0 && Array.isArray(disappearListId) && disappearListId.length > 0) {
        const newMessageList = handleDisappearMsg(messageListRef.current, disappearListId);
        setMessageList(newMessageList);
      }
    },
  });

  const deleteOnlyMeMsgRedux = useSelector(ConversationSelectors.getDeleteOnlyMeMsg);

  const handleIncomingMessage = useCallback(async (incomingMessages, selectedGroupId, isLoadMore) => {
    const isValid = incomingMessages.length > 0 && scrollDataRef.current.isNewest;
    if (isValid) {
      const newMessageList = await handleNewMessageList(incomingMessages, messageListRef.current, selectedGroupId, {
        isLoadMore,
      });

      setMessageList(newMessageList);
    }

    // Scroll to new local message
    const newestMessage = incomingMessages[incomingMessages.length - 1];
    if (isLocalMessage(newestMessage)) {
      scrollToLastMessage();
    }
  }, []);

  useEffect(() => {
    const messageList = incomingMessages ? Object.values(incomingMessages) : null;
    const validMessageList = messageList?.filter(item => !disappearListId.includes(item.id));

    if (Array.isArray(validMessageList) && validMessageList.length > 0) {
      handleIncomingMessage(validMessageList, groupDetailContext.id, isLoadMore);
    }
  }, [incomingMessages]);

  // Handle restore delete only me
  useEffect(() => {
    const { message, isDelete } = deleteOnlyMeMsgRedux;
    if (!message || !message.id) return; // Skip if no message is restored

    let newMessageList = [...messageListRef.current];
    const sendAsDirectMessage =
      convertString2JSON(message?.options, {})?.send_as_direct_message ===
      SystemConstant.THREAD_MSG_SEND_OPTION.sendAsDirect;
    const isMsgInViewMode = false === Boolean(message?.threadId) || sendAsDirectMessage;

    const restoreList = {};
    newMessageList.forEach((item, index) => {
      const isRestoreCalling =
        SystemConstant.ARR_CALLING_TYPES.includes(item.sendType) &&
        [message.parentId, message.sourceId].includes(item.parentId);

      if (item.id === message?.id || item.threadId === message?.sourceId || isRestoreCalling) restoreList[index] = item;
    });

    if (
      message &&
      message.groupId === selectedGroupId &&
      isMsgInViewMode &&
      !isDelete &&
      Object.keys(restoreList).length > 0
    ) {
      Object.keys(restoreList).forEach(restoreIndex => {
        newMessageList[restoreIndex] = {
          ...restoreList[restoreIndex],
          state: SystemConstant.STATE.active,
          isDeleteOnlyMe: false,
        };
      });

      newMessageList.reverse();
      refactorMessageList(newMessageList).then(messageList => setMessageList(messageList.reverse()));
    }
  }, [deleteOnlyMeMsgRedux]);

  return (
    <InfiniteScroll
      id={CHAT_WRAPPER_ID}
      className={classes.wrapChatItem}
      hasMore={isLoadMore}
      isReverse={true}
      ScrollTooltip={ScrollTooltip}
      onScrollToTop={getNewerMessage}
      onScrollToBottom={getOlderMessage}
      onClickScrollButton={scrollToLastMessage}
    >
      {showMessage.map(messageItem => (
        <MessageItem key={messageItem.id} message={messageItem} />
      ))}
    </InfiniteScroll>
  );
};

ViewMode.propTypes = {};
ViewMode.defaultProps = {};

export default memo(ViewMode);

const useStyles = makeStyles({
  wrapChatItem: {
    display: "flex",
    flexDirection: "column-reverse",
    height: "100%",
    padding: "0 22px",
    paddingBottom: 15,
  },
});
